<template>
  <div
    class="ques-pagination"
    :class="{ 'is-docked': docked }"
  >
    <div
      v-if="currPage || totalPage"
      class="ques-pagination__meta"
    >
      <span
        v-if="currPage"
        class="ques-pagination__text is-current"
      >{{ currPageNum }}</span>
      <span
        v-if="currPage && totalPage"
        class="ques-pagination__sep"
      >/</span>
      <span
        v-if="totalPage"
        class="ques-pagination__text is-muted"
      >{{ totalPageNum }}</span>
    </div>
    <div class="ques-pagination__actions">
      <button
        v-if="prev"
        type="button"
        class="ques-pagination__btn is-ghost"
        :disabled="isFirstPage"
        @click="prevClick"
      >
        <i class="el-icon-arrow-left"></i>
        上一页
      </button>
      <button
        v-if="!isLastPage"
        type="button"
        class="ques-pagination__btn is-primary is-grow"
        @click="nextClick"
      >
        下一页
        <i class="el-icon-arrow-right"></i>
      </button>
      <div
        v-else-if="$slots.submit"
        class="ques-pagination__submit is-grow"
      >
        <slot name="submit"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuesPagination',
  props: {
    prev: {
      type: Boolean,
      default: true
    },
    currPage: {
      type: Boolean,
      default: true
    },
    currPageNum: {
      type: Number,
      default: 1
    },
    totalPage: {
      type: Boolean,
      default: true
    },
    totalPageNum: {
      type: Number,
      default: 1
    },
    /** 移动端底部吸底操作栏 */
    docked: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isFirstPage() {
      return this.currPageNum <= 1
    },
    isLastPage() {
      return this.currPageNum >= this.totalPageNum
    }
  },
  methods: {
    prevClick() {
      if (this.isFirstPage) {
        return
      }
      this.$emit('prev', this.currPageNum)
    },
    nextClick() {
      if (this.isLastPage) {
        return
      }
      this.$emit('next', this.currPageNum)
    }
  }
}
</script>

<style scoped lang="scss">
.ques-pagination {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 20px auto 8px;
  padding: 4px 8px;
  max-width: 640px;
  box-sizing: border-box;
}

.ques-pagination__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 10px;
  width: 100%;
}

.ques-pagination__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 96px;
  height: 44px;
  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

.ques-pagination__btn i {
  font-size: 14px;
}

.ques-pagination__btn.is-grow {
  flex: 1;
  min-width: 132px;
}

.ques-pagination__btn.is-ghost {
  color: #64748b;
  background: #fff;
  border-color: #e2e8f0;
  flex: 0 0 auto;
}

.ques-pagination__btn.is-ghost:hover:not(:disabled) {
  color: #334155;
  border-color: #cbd5e1;
  background: #f8fafc;
}

.ques-pagination__btn.is-ghost:disabled {
  color: #cbd5e1;
  background: #f8fafc;
  border-color: #eef2f7;
  cursor: not-allowed;
  opacity: 1;
}

.ques-pagination__btn.is-primary {
  color: #fff;
  background: var(--form-theme-color, #4c4edb);
  border-color: var(--form-theme-color, #4c4edb);
  box-shadow: 0 4px 12px var(--form-theme-color-shadow, rgba(76, 78, 219, 0.18));
}

.ques-pagination__btn.is-primary:hover {
  filter: brightness(1.03);
}

.ques-pagination__btn:active:not(:disabled) {
  opacity: 0.92;
}

.ques-pagination__submit {
  min-width: 132px;
}

.ques-pagination__submit.is-grow {
  flex: 1;
  min-width: 0;
}

.ques-pagination__submit ::v-deep .form-submit-wrap {
  width: 100%;
  padding: 0;
}

.ques-pagination__submit ::v-deep .form-submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  height: 44px;
  padding: 0 16px !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  border-radius: 10px;
  font-size: 15px;
  line-height: 1;
  box-sizing: border-box;
  transform: none;
}

.ques-pagination__submit ::v-deep .form-submit-btn:hover,
.ques-pagination__submit ::v-deep .form-submit-btn:active {
  transform: none;
}

.ques-pagination__submit ::v-deep .form-submit-btn__text {
  line-height: 1;
  vertical-align: middle;
}

.ques-pagination__meta {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  font-variant-numeric: tabular-nums;
  user-select: none;
}

.ques-pagination__text {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  line-height: 1;
}

.ques-pagination__text.is-current {
  font-size: 15px;
  font-weight: 600;
  color: #334155;
}

.ques-pagination__text.is-muted {
  font-weight: 400;
  color: #94a3b8;
}

.ques-pagination__sep {
  font-size: 12px;
  color: #cbd5e1;
  line-height: 1;
}

.ques-pagination.is-docked {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
  margin: 0;
  max-width: none;
  gap: 8px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid rgba(226, 232, 240, 0.95);
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  .ques-pagination__actions {
    justify-content: stretch;
  }

  .ques-pagination__btn {
    height: 44px;
    font-size: 15px;
    border-radius: 10px;
  }

  .ques-pagination__btn.is-primary {
    box-shadow: 0 4px 12px var(--form-theme-color-shadow, rgba(76, 78, 219, 0.18));
  }

  .ques-pagination__btn.is-primary:hover {
    filter: none;
  }
}

@media screen and (max-width: 768px) {
  .ques-pagination:not(.is-docked) {
    margin: 16px auto 6px;
    padding: 0 4px;
    max-width: none;
  }

  .ques-pagination:not(.is-docked) .ques-pagination__btn {
    min-width: 88px;
    height: 44px;
    padding: 0 14px;
    font-size: 14px;
    border-radius: 10px;
  }

  .ques-pagination:not(.is-docked) .ques-pagination__btn.is-primary {
    box-shadow: 0 4px 12px var(--form-theme-color-shadow, rgba(76, 78, 219, 0.18));
  }
}
</style>
